import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, PhoneIncoming, Phone, Settings, Search,
  ChevronRight, Clock, ThumbsUp, ThumbsDown, Minus,
  Play, Shield, User, Mail,
  Loader2, Check, Bot, Zap, Activity, Radio, PhoneOff
} from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const INTENT_STYLES = {
  lead: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Lead' },
  appointment: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Appointment' },
  inquiry: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'Inquiry' },
  support: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Support' },
  spam: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Spam' },
  other: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Other' },
  unknown: { bg: 'bg-gray-500/15', text: 'text-gray-500', label: 'Pending' },
};

const SENTIMENT_ICON = {
  positive: <ThumbsUp className="w-3 h-3 text-emerald-400" />,
  neutral: <Minus className="w-3 h-3 text-gray-400" />,
  negative: <ThumbsDown className="w-3 h-3 text-red-400" />,
};

const InboundCalls = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const headers = { Authorization: `Bearer ${token}` };

  const [view, setView] = useState('calls');
  const [callLogs, setCallLogs] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [aiSettings, setAiSettings] = useState({
    greeting: '',
    ai_enabled: true,
    auto_qualify: true,
    record_calls: true,
    spam_filter: true,
    forward_to: '',
    business_hours_only: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, analyticsRes, phonesRes, settingsRes, activeRes] = await Promise.allSettled([
        axios.get(`${API}/calls/logs`, { headers }),
        axios.get(`${API}/calls/analytics`, { headers }),
        axios.get(`${API}/phone/my-numbers`, { headers }),
        axios.get(`${API}/calls/inbound-settings`, { headers }),
        axios.get(`${API}/realtime-voice/active-calls`, { headers }),
      ]);
      if (logsRes.status === 'fulfilled') setCallLogs(logsRes.value.data);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      if (phonesRes.status === 'fulfilled') setPhoneNumbers(phonesRes.value.data);
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        setAiSettings(prev => ({ ...prev, ...settingsRes.value.data }));
      }
      if (activeRes.status === 'fulfilled') setActiveCalls(activeRes.value.data.calls || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll active calls every 10 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/realtime-voice/active-calls`, { headers });
        setActiveCalls(res.data.calls || []);
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/calls/inbound-settings`, aiSettings, { headers });
      toast({ title: 'Inbound call settings saved' });
    } catch {
      toast({ title: 'Failed to save settings', variant: 'destructive' });
    } finally { setSavingSettings(false); }
  };

  const endCall = async (callSid) => {
    try {
      await axios.post(`${API}/realtime-voice/end-call/${callSid}`, {}, { headers });
      toast({ title: 'Call ended' });
      setActiveCalls(prev => prev.filter(c => c.id !== callSid));
    } catch {
      toast({ title: 'Failed to end call', variant: 'destructive' });
    }
  };

  const filteredCalls = callLogs.filter(c =>
    !searchTerm ||
    (c.caller_number || '').includes(searchTerm) ||
    (c.summary || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.caller_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { key: 'calls', label: 'Call Log', icon: PhoneIncoming },
    { key: 'settings', label: 'AI Settings', icon: Bot },
    { key: 'numbers', label: 'Phone Numbers', icon: Phone },
  ];

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="inbound-calls-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {view === 'detail' && (
            <Button variant="ghost" onClick={() => setView('calls')} className="text-gray-400 hover:text-white p-2" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold" data-testid="inbound-calls-title">Inbound Calls</h1>
            <p className="text-gray-500 text-sm">AI-powered real-time call handling</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20" data-testid="realtime-badge">
            <Zap className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs text-emerald-400 font-medium">Real-time AI</span>
          </div>
        </div>

        {/* Active Calls Banner */}
        {activeCalls.length > 0 && (
          <Card className="bg-emerald-500/5 border-emerald-500/20 mb-6" data-testid="active-calls-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  <Radio className="w-5 h-5 text-emerald-400" />
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
                </div>
                <span className="text-sm font-medium text-emerald-400">{activeCalls.length} Active Call{activeCalls.length > 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-2">
                {activeCalls.map(call => (
                  <div key={call.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]" data-testid={`active-call-${call.id}`}>
                    <div className="flex items-center gap-3">
                      <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                      <div>
                        <p className="text-sm text-white font-medium">{call.caller_number || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{call.direction === 'inbound' ? 'Inbound' : 'Outbound'} &middot; Started {call.created_at ? new Date(call.created_at).toLocaleTimeString() : ''}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => endCall(call.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`end-call-${call.id}`}>
                      <PhoneOff className="w-4 h-4 mr-1" /> End
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="inbound-analytics">
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold">{analytics.total_calls || callLogs.length}</p>
            <p className="text-xs text-gray-500">Total Calls</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-400">{analytics.leads_captured || callLogs.filter(c => c.intent === 'lead').length}</p>
            <p className="text-xs text-gray-500">Leads Captured</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-400">{analytics.appointments || callLogs.filter(c => c.intent === 'appointment').length}</p>
            <p className="text-xs text-gray-500">Appointments</p>
          </CardContent></Card>
          <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="p-4">
            <p className="text-2xl font-bold text-red-400">{analytics.spam_blocked || callLogs.filter(c => c.intent === 'spam').length}</p>
            <p className="text-xs text-gray-500">Spam Blocked</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/[0.03] rounded-lg p-1 w-fit" data-testid="inbound-tabs">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setView(tab.key)} data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${view === tab.key || (view === 'detail' && tab.key === 'calls') ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* CALL LOG */}
        {(view === 'calls') && (
          <div data-testid="calls-view">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search calls..." className="pl-10 bg-white/[0.03] border-white/[0.06] text-white" data-testid="search-calls" />
              </div>
            </div>

            {loading ? <p className="text-gray-500 text-sm py-8 text-center">Loading...</p> :
              filteredCalls.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="py-16 text-center">
                  <PhoneIncoming className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-400 mb-1">No inbound calls yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Your AI agent is ready! When someone calls your Twilio number, the AI will answer in real-time with natural voice conversation.</p>
                  <Button onClick={() => setView('numbers')} className="bg-blue-600 hover:bg-blue-700 text-white"><Phone className="w-4 h-4 mr-2" /> Manage Numbers</Button>
                </CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {filteredCalls.map(call => {
                    const intent = INTENT_STYLES[call.intent] || INTENT_STYLES.unknown;
                    const isRealtime = call.mode === 'realtime';
                    return (
                      <Card key={call.id || call.call_sid} className="bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] transition-colors cursor-pointer" data-testid={`call-${call.id || call.call_sid}`}>
                        <CardContent className="p-4 flex items-center gap-4" onClick={() => { setSelectedCall(call); setView('detail'); }}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${call.intent === 'spam' ? 'bg-red-600/20 text-red-400' : 'bg-blue-600/20 text-blue-400'}`}>
                            {call.intent === 'spam' ? <Shield className="w-5 h-5" /> : <PhoneIncoming className="w-5 h-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-white truncate">{call.caller_name || call.caller_number || 'Unknown Caller'}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${intent.bg} ${intent.text}`}>{intent.label}</span>
                              {isRealtime && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">RT</span>}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{call.summary || 'No summary available'}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {call.sentiment && <span className="flex items-center">{SENTIMENT_ICON[call.sentiment] || SENTIMENT_ICON.neutral}</span>}
                            <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{call.created_at ? new Date(call.created_at).toLocaleDateString() : ''}</span>
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
          </div>
        )}

        {/* CALL DETAIL */}
        {view === 'detail' && selectedCall && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="call-detail">
            <div className="lg:col-span-2">
              <Card className="bg-white/[0.03] border-white/[0.06]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <PhoneIncoming className="w-4 h-4 text-blue-400" />
                    Call from {selectedCall.caller_name || selectedCall.caller_number || 'Unknown'}
                    {selectedCall.mode === 'realtime' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 ml-2">Real-time AI</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedCall.summary && (
                    <div className="p-3 bg-white/[0.03] rounded-lg mb-4">
                      <p className="text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">AI Summary</p>
                      <p className="text-sm text-gray-200 leading-relaxed">{selectedCall.summary}</p>
                    </div>
                  )}
                  <div className="h-[350px] overflow-y-auto space-y-3 pr-2" data-testid="transcript-container">
                    {(selectedCall.transcript_entries || []).length > 0 ? (
                      selectedCall.transcript_entries.map((entry, i) => (
                        <div key={i} className={`flex ${entry.role === 'caller' || entry.role === 'user' || entry.speaker === 'caller' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            entry.role === 'caller' || entry.role === 'user' || entry.speaker === 'caller'
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-white/[0.06] text-gray-200 rounded-bl-sm'
                          }`}>
                            <p className="text-[10px] font-medium mb-1 opacity-60">
                              {entry.role === 'caller' || entry.role === 'user' || entry.speaker === 'caller' ? 'Caller' : 'AI Agent'}
                            </p>
                            {entry.text || entry.content}
                            {entry.response && (
                              <>
                                <div className="mt-3 pt-2 border-t border-white/10">
                                  <p className="text-[10px] font-medium mb-1 opacity-60">AI Agent</p>
                                  {entry.response}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-8">No transcript available for this call.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            <Card className="bg-white/[0.03] border-white/[0.06]" data-testid="call-info">
              <CardHeader><CardTitle className="text-white text-sm">Call Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {selectedCall.caller_number && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedCall.caller_number}</span></div>}
                  {selectedCall.caller_name && <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedCall.caller_name}</span></div>}
                  {selectedCall.caller_email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-500" /><span className="text-gray-300">{selectedCall.caller_email}</span></div>}
                </div>
                {(selectedCall.intent || selectedCall.sentiment) && (
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">AI Analysis</p>
                    <div className="space-y-2 text-xs">
                      {selectedCall.intent && (() => {
                        const intent = INTENT_STYLES[selectedCall.intent] || INTENT_STYLES.unknown;
                        return <div className="flex justify-between"><span className="text-gray-500">Intent</span><span className={`px-2 py-0.5 rounded-full ${intent.bg} ${intent.text}`}>{intent.label}</span></div>;
                      })()}
                      {selectedCall.sentiment && <div className="flex justify-between items-center"><span className="text-gray-500">Sentiment</span><span className="flex items-center gap-1">{SENTIMENT_ICON[selectedCall.sentiment]} <span className="text-gray-400 capitalize">{selectedCall.sentiment}</span></span></div>}
                      {selectedCall.mode && <div className="flex justify-between"><span className="text-gray-500">Mode</span><span className="text-emerald-400 capitalize">{selectedCall.mode === 'realtime' ? 'Real-time AI' : 'Standard'}</span></div>}
                    </div>
                  </div>
                )}
                {selectedCall.tags && selectedCall.tags.length > 0 && (
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedCall.tags.map((tag, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/10">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-white/5">
                  <p className="text-xs text-gray-500">Date: {selectedCall.created_at ? new Date(selectedCall.created_at).toLocaleString() : 'N/A'}</p>
                  <p className="text-xs text-gray-500">Duration: {formatDuration(selectedCall.duration)}</p>
                  {selectedCall.direction && <p className="text-xs text-gray-500">Direction: <span className="capitalize">{selectedCall.direction}</span></p>}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI SETTINGS */}
        {view === 'settings' && (
          <div className="max-w-2xl" data-testid="inbound-settings">
            <Card className="bg-white/[0.03] border-white/[0.06]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-blue-400" /> AI Call Agent Settings
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 ml-2">Real-time Voice</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-emerald-400" />
                    <p className="text-sm font-medium text-emerald-400">Real-time AI Powered by OpenAI</p>
                  </div>
                  <p className="text-xs text-gray-400">Your AI agent uses OpenAI's Realtime API for natural, low-latency voice conversations. Callers experience sub-second response times with human-like speech.</p>
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">AI Call Handling</Label><p className="text-gray-500 text-xs">Let AI answer and qualify inbound calls</p></div>
                  <Switch checked={aiSettings.ai_enabled} onCheckedChange={v => setAiSettings(p => ({ ...p, ai_enabled: v }))} data-testid="ai-enabled-toggle" />
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">AI Greeting</Label>
                  <Textarea value={aiSettings.greeting} onChange={e => setAiSettings(p => ({ ...p, greeting: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1 min-h-[80px]" data-testid="ai-greeting" placeholder="Leave empty to use your Business Setup greeting (automatically mentions your business name)" />
                  <p className="text-gray-600 text-xs mt-1">This is the first thing the AI says when answering a call</p>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Forward Calls To</Label>
                  <Input value={aiSettings.forward_to} onChange={e => setAiSettings(p => ({ ...p, forward_to: e.target.value }))} className="bg-black/30 border-white/10 text-white mt-1" data-testid="forward-to" placeholder="+1 (555) 123-4567" />
                  <p className="text-gray-600 text-xs mt-1">Forward qualified leads to this number</p>
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Auto-Qualify Leads</Label><p className="text-gray-500 text-xs">AI classifies calls as leads, appointments, etc.</p></div>
                  <Switch checked={aiSettings.auto_qualify} onCheckedChange={v => setAiSettings(p => ({ ...p, auto_qualify: v }))} data-testid="auto-qualify-toggle" />
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Spam Filter</Label><p className="text-gray-500 text-xs">Automatically detect and block spam calls</p></div>
                  <Switch checked={aiSettings.spam_filter} onCheckedChange={v => setAiSettings(p => ({ ...p, spam_filter: v }))} data-testid="spam-filter-toggle" />
                </div>

                <div className="flex items-center justify-between">
                  <div><Label className="text-gray-300">Record Calls</Label><p className="text-gray-500 text-xs">Record and transcribe all inbound calls</p></div>
                  <Switch checked={aiSettings.record_calls} onCheckedChange={v => setAiSettings(p => ({ ...p, record_calls: v }))} data-testid="record-calls-toggle" />
                </div>

                <div className="rounded-md border border-white/[0.08] bg-white/[0.03] p-3" data-testid="answering-policy-info">
                  <Label className="text-gray-300">24/7 AI Answering</Label>
                  <p className="text-gray-500 text-xs mt-1">Mia answers every call around the clock. Transfers to your team only happen during the business hours set in your Business Profile.</p>
                </div>

                <Button onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="save-inbound-settings-btn">
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PHONE NUMBERS */}
        {view === 'numbers' && (
          <div data-testid="phone-numbers-view">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Your Phone Numbers</h2>
              <Button onClick={() => navigate('/phone-setup')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-number-btn">
                <Phone className="w-4 h-4 mr-2" /> Add Number
              </Button>
            </div>

            {phoneNumbers.length === 0 ? (
              <Card className="bg-white/[0.03] border-white/[0.06]"><CardContent className="py-16 text-center">
                <Phone className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-gray-400 mb-1">No phone numbers</h3>
                <p className="text-sm text-gray-500 mb-4">Purchase or add a phone number to start receiving AI-powered calls.</p>
                <Button onClick={() => navigate('/phone-setup')} className="bg-blue-600 hover:bg-blue-700 text-white"><Phone className="w-4 h-4 mr-2" /> Set Up Phone</Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {phoneNumbers.map(num => (
                  <Card key={num.id} className="bg-white/[0.03] border-white/[0.06]" data-testid={`phone-${num.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 flex-shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{num.phone_number}</p>
                        <p className="text-xs text-gray-500">{num.friendly_name || 'AI Receptionist Line'} &middot; Real-time AI Enabled</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${num.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-400'}`}>
                        {num.status || 'active'}
                      </span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboundCalls;
